import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  listDemoRequests,
  updateDemoRequestStatus,
  deleteDemoRequest,
  adminMe,
} from "../lib/adminApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Inbox, Search, ChevronRight, Globe, Mail as MailIcon, Building2,
  FileText, Trash2, History as HistoryIcon, Loader2,
} from "lucide-react";
import AuditMeta from "../components/AuditMeta";

// Lightweight lead-tracking statuses. Mirrors `ALLOWED_LEAD_STATUSES` on the
// backend (admin_routes.py). The badge palette is deliberately muted — this
// is an operational intake, not a sales pipeline visualization.
const STATUSES = [
  "New", "Contacted", "Demo Scheduled", "Qualified",
  "Proposal Shared", "Converted", "Closed",
];

const STATUS_STYLE = {
  "New":             "bg-[#E0F2FE] text-[#025979] border border-[#025979]/20",
  "Contacted":       "bg-[#FEF3C7] text-[#92400E] border border-[#92400E]/20",
  "Demo Scheduled":  "bg-[#EDE9FE] text-[#5B21B6] border border-[#5B21B6]/20",
  "Qualified":       "bg-[#DCFCE7] text-[#166534] border border-[#166534]/20",
  "Proposal Shared": "bg-[#DBEAFE] text-[#1E40AF] border border-[#1E40AF]/20",
  "Converted":       "bg-[#ECFDF5] text-[#047857] border border-[#047857]/20",
  "Closed":          "bg-[#F3F4F6] text-[#4B5563] border border-[#4B5563]/20",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const date = d.toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "2-digit", minute: "2-digit",
    });
    return `${date} · ${time}`;
  } catch { return iso; }
}

export default function DemoRequestsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openLead, setOpenLead] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedRef = useRef(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // Pending status change awaiting a mandatory note: { lead, nextStatus }.
  const [statusChange, setStatusChange] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  // Lead pending deletion (admin-only): the lead object.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listDemoRequests();
      setLeads(data || []);
    } catch (e) {
      toast.error("Failed to load demo requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Determine the current user's role — only admins may delete leads.
  useEffect(() => {
    let active = true;
    adminMe()
      .then((u) => { if (active) setIsAdmin((u?.role || "").toLowerCase() === "admin"); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Deep-link from internal notification email: /consultant/demo-requests?lead=<id>
  // Open the matching row in a detail modal so the consultant can act on it.
  useEffect(() => {
    const leadId = searchParams.get("lead");
    if (!leadId || leads.length === 0) return;
    const match = leads.find((l) => l.id === leadId);
    if (match) {
      setOpenLead(match);
      // Scroll the highlighted row into view after the modal closes.
      setTimeout(() => {
        if (focusedRef.current) {
          focusedRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      }, 50);
    }
  }, [leads, searchParams]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "all" && (l.status || "New") !== statusFilter) return false;
      if (!q) return true;
      return [
        l.full_name, l.company_name, l.business_email,
        l.country_region, l.solution, l.notes, l.source_path,
      ].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [leads, query, statusFilter]);

  const counts = useMemo(() => {
    const out = { all: leads.length };
    for (const s of STATUSES) out[s] = 0;
    for (const l of leads) {
      const s = l.status || "New";
      out[s] = (out[s] || 0) + 1;
    }
    return out;
  }, [leads]);

  // Selecting a new status opens the note dialog — the change is only applied
  // after a mandatory note is provided.
  const requestStatusChange = (lead, nextStatus) => {
    if (!lead || nextStatus === (lead.status || "New")) return;
    setNoteText("");
    setStatusChange({ lead, nextStatus });
  };

  const confirmStatusChange = async () => {
    if (!statusChange) return;
    const note = noteText.trim();
    if (!note) { toast.error("A note is required for every status change."); return; }
    const { lead, nextStatus } = statusChange;
    setSavingStatus(true);
    try {
      const updated = await updateDemoRequestStatus(lead.id, nextStatus, note);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...updated } : l)));
      if (openLead?.id === lead.id) setOpenLead((o) => ({ ...o, ...updated }));
      toast.success(`Status set to ${nextStatus}`);
      setStatusChange(null);
      setNoteText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDemoRequest(deleteTarget.id);
      setLeads((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      if (openLead?.id === deleteTarget.id) setOpenLead(null);
      toast.success("Demo request deleted");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete demo request");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="demo-requests-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1
            className="text-xl font-bold text-[#1F2937]"
            style={{ fontFamily: "Chivo" }}
            data-testid="demo-requests-title"
          >
            Demo Requests
          </h1>
          <p className="text-[11px] text-[#6B7280] mt-1 max-w-2xl leading-snug">
            Leads captured via the public &ldquo;Request Demo&rdquo; form. Triage status
            stays in sync with internal notification emails — no separate CRM
            required.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="demo-requests-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, company, email…"
              className="h-9 pl-7 pr-3 rounded-sm border border-[#E5E7EB] text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#025979] w-72"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="demo-requests-status-filter"
              className="h-9 w-44 rounded-sm text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="demo-requests-filter-all">
                All statuses ({counts.all})
              </SelectItem>
              {STATUSES.map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  data-testid={`demo-requests-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {s} ({counts[s] || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
        <table className="w-full text-xs" data-testid="demo-requests-table">
          <thead>
            <tr className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Lead</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Company</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Solution Interest</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Submitted</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium w-44">Status</th>
              <th className="text-right px-4 py-3 text-[#6B7280] font-medium w-24">Open</th>
            </tr>
          </thead>
          <tbody data-testid="demo-requests-tbody">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-[#9CA3AF]">
                  Loading leads…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-[#9CA3AF]">
                  <Inbox className="w-6 h-6 mx-auto mb-2 text-[#CBD5E1]" />
                  No demo requests {query || statusFilter !== "all" ? "match the current filter" : "yet"}.
                </td>
              </tr>
            ) : (
              filtered.map((l) => {
                const highlighted = searchParams.get("lead") === l.id;
                return (
                  <tr
                    key={l.id}
                    ref={highlighted ? focusedRef : null}
                    className={`border-b border-[#E5E7EB] last:border-b-0 ${highlighted ? "bg-[#F0F9FF]" : ""}`}
                    data-testid={`demo-request-row-${l.id}`}
                  >
                    <td className="px-4 py-3">
                      <p className="text-[#1F2937] font-medium">{l.full_name || "—"}</p>
                      <p className="text-[11px] text-[#6B7280] flex items-center gap-1 mt-0.5">
                        <MailIcon className="w-3 h-3" />
                        {l.business_email || "—"}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-[#1F2937]">{l.company_name || "—"}</p>
                      <p className="text-[11px] text-[#6B7280] flex items-center gap-1 mt-0.5">
                        <Globe className="w-3 h-3" />
                        {l.country_region || "—"}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-[#4B5563]">{l.solution || "—"}</td>
                    <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap">
                      {fmtDate(l.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={l.status || "New"}
                        onValueChange={(v) => requestStatusChange(l, v)}
                      >
                        <SelectTrigger
                          className={`h-7 w-full rounded-sm text-[11px] font-medium ${STATUS_STYLE[l.status || "New"] || STATUS_STYLE["New"]}`}
                          data-testid={`demo-request-status-trigger-${l.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem
                              key={s}
                              value={s}
                              data-testid={`demo-request-status-${l.id}-${s.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setOpenLead(l)}
                        className="inline-flex items-center gap-1 text-[#025979] hover:text-[#03738C] transition-colors"
                        data-testid={`demo-request-open-${l.id}`}
                      >
                        View <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={!!openLead}
        onOpenChange={(v) => {
          if (!v) {
            setOpenLead(null);
            // Drop the ?lead query param when the modal closes so refresh
            // doesn't reopen it.
            if (searchParams.get("lead")) {
              const next = new URLSearchParams(searchParams);
              next.delete("lead");
              setSearchParams(next, { replace: true });
            }
          }
        }}
      >
        <DialogContent
          className="max-w-2xl rounded-sm"
          data-testid="demo-request-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#025979]" />
              {openLead?.full_name || "Lead"} · {openLead?.company_name || "—"}
            </DialogTitle>
            <DialogDescription>
              Lead intake from the public Request Demo form. Update status to
              keep the team aligned.
            </DialogDescription>
          </DialogHeader>
          {openLead && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
                <Detail label="Business Email" value={openLead.business_email} mail />
                <Detail label="Country / Region" value={openLead.country_region} />
                <Detail label="Solution Interest" value={openLead.solution} />
                <Detail label="Submitted" value={fmtDate(openLead.created_at)} />
                <div>
                  <p className="text-[11px] text-[#6B7280] uppercase tracking-wide font-medium mb-1">
                    Status
                  </p>
                  <Select
                    value={openLead.status || "New"}
                    onValueChange={(v) => requestStatusChange(openLead, v)}
                  >
                    <SelectTrigger
                      className={`h-8 w-full rounded-sm text-xs font-medium ${STATUS_STYLE[openLead.status || "New"] || STATUS_STYLE["New"]}`}
                      data-testid="demo-request-detail-status-trigger"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <p className="text-[11px] text-[#6B7280] uppercase tracking-wide font-medium mb-1 flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Notes
                </p>
                <div className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-3 text-xs text-[#1F2937] whitespace-pre-wrap min-h-[60px]">
                  {(openLead.notes || "").trim() || (
                    <span className="text-[#9CA3AF]">No notes submitted.</span>
                  )}
                </div>
              </div>
              {/* Send Email action temporarily hidden — the default mailto
                  handler on some browsers opens a blank tab instead of the
                  user's mail client. Re-enable once we ship a built-in
                  email compose flow (or document a per-browser handler setup). */}
              {false && openLead.business_email && (() => {
                // Compose a friendly default email so the consultant can hit
                // "Send" immediately. `mailto:` is RFC-compliant — the
                // browser hands off to the OS mail client (Outlook / Mail /
                // Gmail web depending on the user's protocol handler).
                const firstName = (openLead.full_name || "").trim().split(/\s+/)[0] || "";
                const solution = (openLead.solution || "").trim();
                const subject = "PrimeS4 Demo Request Follow-up";
                const body = [
                  `Hi ${firstName || "there"},`,
                  "",
                  `Thank you for your interest in PrimeS4. I wanted to follow up on your demo request${
                    solution ? ` for ${solution}` : ""
                  }.`,
                ].join("\n");
                const href =
                  `mailto:${openLead.business_email}` +
                  `?subject=${encodeURIComponent(subject)}` +
                  `&body=${encodeURIComponent(body)}`;
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#025979] hover:text-[#03738C] border border-[#E5E7EB] hover:border-[#025979]/40 bg-white hover:bg-[#F0F9FF] rounded-sm px-3 py-1.5 transition-colors"
                    data-testid="demo-request-detail-send-email"
                  >
                    <MailIcon className="w-3.5 h-3.5" />
                    Send Email
                  </a>
                );
              })()}
              <div className="pt-3 mt-1 border-t border-[#E5E7EB]">
                <p className="text-[11px] text-[#6B7280] uppercase tracking-wide font-medium mb-2 flex items-center gap-1">
                  <HistoryIcon className="w-3 h-3" />
                  Status History
                </p>
                {(openLead.status_history || []).length === 0 ? (
                  <p className="text-xs text-[#9CA3AF]" data-testid="demo-request-history-empty">
                    No status changes recorded yet.
                  </p>
                ) : (
                  <ul className="space-y-2" data-testid="demo-request-history">
                    {[...(openLead.status_history || [])].reverse().map((h, i) => (
                      <li key={i} className="rounded-sm border border-[#E5E7EB] bg-white p-2.5 text-xs">
                        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                          <span className="text-[#6B7280]">{h.from_status || "—"}</span>
                          <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
                          <span className={`px-1.5 py-0.5 rounded-sm font-medium ${STATUS_STYLE[h.to_status] || STATUS_STYLE["New"]}`}>
                            {h.to_status}
                          </span>
                          <span className="text-[#9CA3AF] ml-auto">{fmtDate(h.at)}</span>
                        </div>
                        <p className="text-[#1F2937] mt-1.5 whitespace-pre-wrap">{h.note}</p>
                        {h.by && <p className="text-[10px] text-[#9CA3AF] mt-1">by {h.by}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="pt-3 mt-1 border-t border-[#E5E7EB] flex items-center justify-between gap-3">
                <AuditMeta
                  record={openLead}
                  testId="demo-request-detail-audit"
                />
                {isAdmin && (
                  <Button
                    variant="outline"
                    onClick={() => setDeleteTarget(openLead)}
                    className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-sm text-xs h-8 shrink-0"
                    data-testid="demo-request-delete-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mandatory-note dialog for every status change */}
      <Dialog open={!!statusChange} onOpenChange={(o) => { if (!o) setStatusChange(null); }}>
        <DialogContent className="max-w-md" data-testid="demo-request-status-note-dialog">
          <DialogHeader>
            <DialogTitle>Update status</DialogTitle>
            <DialogDescription>
              {statusChange && (
                <>Changing <strong>{statusChange.lead.status || "New"}</strong> → <strong>{statusChange.nextStatus}</strong>. A note is required and saved to the history log.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#1F2937]">Note (required)</label>
            <Textarea
              data-testid="demo-request-status-note-input"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Why is the status changing? (e.g. contacted client, scheduled demo, not a fit)"
              rows={4}
              className="rounded-sm text-sm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusChange(null)} className="rounded-sm text-sm h-9" data-testid="demo-request-status-note-cancel">
              Cancel
            </Button>
            <Button
              onClick={confirmStatusChange}
              disabled={savingStatus || !noteText.trim()}
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-sm h-9"
              data-testid="demo-request-status-note-confirm"
            >
              {savingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin-only delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="demo-request-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this demo request?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>This permanently deletes the lead from <strong>{deleteTarget.full_name || deleteTarget.business_email || "this contact"}</strong>, including its status history. This cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="demo-request-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="demo-request-delete-confirm"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Detail({ label, value, mail }) {
  const display = (value || "").trim() || "—";
  return (
    <div>
      <p className="text-[11px] text-[#6B7280] uppercase tracking-wide font-medium mb-1">
        {label}
      </p>
      {mail && display !== "—" ? (
        <a href={`mailto:${display}`} className="text-[#025979] hover:text-[#03738C] break-all">
          {display}
        </a>
      ) : (
        <p className="text-[#1F2937] break-words">{display}</p>
      )}
    </div>
  );
}
